import WMSCapabilities from "ol/format/WMSCapabilities.js";
import { transformExtent } from "ol/proj.js";
import { QGIS_PROJECT_URL } from "config/qgis";
import { fetchQgisResource } from "helpers/qgisClient";
import { MONITORED_NETWORK_LAYER_NAME } from "../layers/monitoredNetwork";

const WEB_MERCATOR = "EPSG:3857";
const WGS84 = "EPSG:4326";

let monitoredNetworkExtentPromise;

const isValidExtent = (extent) =>
  Array.isArray(extent) &&
  extent.length === 4 &&
  extent.every(Number.isFinite) &&
  extent[0] < extent[2] &&
  extent[1] < extent[3];

const findLayer = (layer, layerName) => {
  if (!layer) return undefined;
  if (layer.Name === layerName) return layer;

  return layer.Layer?.map((child) => findLayer(child, layerName)).find(
    Boolean
  );
};

const normalizeCrs = (crs = "") => crs.toUpperCase();

const transformGeographicExtent = (extent) =>
  transformExtent(extent, WGS84, WEB_MERCATOR);

export const extractMonitoredNetworkExtent = (capabilities) => {
  const layer = findLayer(
    capabilities?.Capability?.Layer,
    MONITORED_NETWORK_LAYER_NAME
  );
  if (!layer) return null;

  if (isValidExtent(layer.EX_GeographicBoundingBox)) {
    return transformGeographicExtent(layer.EX_GeographicBoundingBox);
  }

  const boundingBoxes = Array.isArray(layer.BoundingBox)
    ? layer.BoundingBox
    : [];
  const webMercatorBox = boundingBoxes.find(
    ({ crs, srs }) => normalizeCrs(crs || srs) === WEB_MERCATOR
  );
  if (isValidExtent(webMercatorBox?.extent)) {
    return [...webMercatorBox.extent];
  }

  const geographicBox = boundingBoxes.find(({ crs, srs }) => {
    const projection = normalizeCrs(crs || srs);
    return projection === WGS84 || projection === "CRS:84";
  });
  if (!isValidExtent(geographicBox?.extent)) return null;

  const projection = normalizeCrs(
    geographicBox.crs || geographicBox.srs
  );
  const usesWms13LatitudeFirstAxis =
    projection === WGS84 &&
    Boolean(geographicBox.crs) &&
    String(capabilities.version).startsWith("1.3");
  const geographicExtent = usesWms13LatitudeFirstAxis
    ? [
        geographicBox.extent[1],
        geographicBox.extent[0],
        geographicBox.extent[3],
        geographicBox.extent[2],
      ]
    : geographicBox.extent;

  return isValidExtent(geographicExtent)
    ? transformGeographicExtent(geographicExtent)
    : null;
};

const capabilitiesUrl = (projectUrl) => {
  if (!projectUrl) return undefined;

  const url = new URL(projectUrl);
  url.searchParams.set("SERVICE", "WMS");
  url.searchParams.set("REQUEST", "GetCapabilities");
  url.searchParams.set("VERSION", "1.3.0");
  return url.toString();
};

export const getMonitoredNetworkExtent = (
  projectUrl = QGIS_PROJECT_URL
) => {
  if (!projectUrl) return Promise.resolve(null);
  if (monitoredNetworkExtentPromise) return monitoredNetworkExtentPromise;

  monitoredNetworkExtentPromise = fetchQgisResource(
    capabilitiesUrl(projectUrl),
    { headers: { Accept: "application/xml,text/xml" } }
  )
    .then((response) => response.text())
    .then((xml) => new WMSCapabilities().read(xml))
    .then(extractMonitoredNetworkExtent)
    .catch((error) => {
      monitoredNetworkExtentPromise = undefined;
      throw error;
    });

  return monitoredNetworkExtentPromise;
};

export const resetMonitoredNetworkExtentCache = () => {
  monitoredNetworkExtentPromise = undefined;
};
